import { Component, EventEmitter, inject, Output, Signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { faPen, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { RemoveConditionalsRequest } from '../../../models/conditions/remove-conditionals-request.model';
import { ISourceForm } from '../../../models/sources/source-form.model';
import { SelectableVariableRequireType } from '../../../models/sources/variable-required-type.constants';
import { SelectableVariableValueType } from '../../../models/sources/variable-value-type.constants';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { SourceVariableDialogComponent } from '../source-variable-dialog/source-variable-dialog.component';

@Component({
  selector: 'empathy-source-form',
  templateUrl: 'source-form.component.html',
  styleUrls: ['./source-form.component.scss']
})
export class SourceFormComponent {
  @Output() addSource = new EventEmitter();
  @Output() remove = new EventEmitter();

  activeSourceGrp$: Signal<FormGroup<ISourceForm>>;

  faEyeSlash = faEyeSlash;
  faPen = faPen;
  faPlus = faPlus;
  faTrash = faTrash;

  listType = QuestionType.SingleChoice;

  requiredTypeLabels: Record<string, string> = Object.fromEntries(SelectableVariableRequireType.map((option) => [option.value, option.text]));
  valueTypeLabels: Record<string, string> = Object.fromEntries(SelectableVariableValueType.map((option) => [option.value, option.text]));

  private dialog = inject(MatDialog);
  private surveyConditionalService = inject(SurveyConditionalsService);
  private surveyEditorService = inject(SurveyEditorService);

  constructor() {
    this.activeSourceGrp$ = this.surveyEditorService.surveyEditor$().sources.active$;
  }

  addVariable(): void {
    this.dialog
      .open(SourceVariableDialogComponent, { data: { takenTokens: this.getTakenTokens() } })
      .afterClosed()
      .subscribe((variable: Question) => {
        if (!variable) {
          return;
        }

        this.surveyEditorService.surveyEditor$().sources.pushVariable(variable);
      });
  }

  onEditVariable(index: number): void {
    const variableCtrl = this.activeSourceGrp$().controls.variables.at(index);
    const variableId = variableCtrl.value.id;
    const previousOptionIds = (variableCtrl.value.options ?? []).map((option) => option.id);

    this.dialog
      .open(SourceVariableDialogComponent, {
        data: {
          takenTokens: this.getTakenTokens(index),
          variable: variableCtrl.value as Question,
          optionsInUse: this.getOptionsInUse(variableId, previousOptionIds)
        }
      })
      .afterClosed()
      .subscribe((variable: Question) => {
        if (!variable) {
          return;
        }

        this.applyVariableEdit(index, variableId, previousOptionIds, variable);
      });
  }

  onRemoveVariable(index: number): void {
    const idToRemove = this.activeSourceGrp$().controls.variables.at(index).value.id;

    this.buildConfirmDialog(idToRemove)
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.surveyEditorService.surveyEditor$().sources.removeVariable(index);
          this.surveyConditionalService.removeAffectedConditionsById(idToRemove);
          this.surveyConditionalService.removeAffectedHiddenConditionsById(idToRemove);
        }
      });
  }

  private applyVariableEdit(index: number, variableId: string, previousOptionIds: string[], variable: Question): void {
    const surveyEditor = this.surveyEditorService.surveyEditor$();
    surveyEditor.sources.updateVariable(index, variable);

    const keptOptionIds = variable.type === QuestionType.SingleChoice ? (variable.options ?? []).map((option) => option.id) : [];

    previousOptionIds
      .filter((optionId) => !keptOptionIds.includes(optionId))
      .forEach((optionId) => {
        const affectedQuestionIds = surveyEditor.affectedQuestionsOnConditionByOptionId(variableId, optionId);
        const affectedHiddenQuestionIds = surveyEditor.affectedHiddenQuestionsOnConditionByHiddenOptionId(variableId, optionId);

        this.surveyConditionalService.removeHiddenConditions(new RemoveConditionalsRequest(variableId, affectedHiddenQuestionIds, optionId));
        affectedQuestionIds.forEach((questionId) =>
          this.surveyConditionalService.removeConditions(new RemoveConditionalsRequest(questionId, [variableId], optionId))
        );
      });
  }

  private getOptionsInUse(variableId: string, optionIds: string[]): Record<string, string[]> {
    const surveyEditor = this.surveyEditorService.surveyEditor$();

    const entries = optionIds
      .map<
        [string, string[]]
      >((optionId) => [optionId, [...surveyEditor.affectedQuestionsOnConditionByOptionId(variableId, optionId), ...surveyEditor.affectedHiddenQuestionsOnConditionByHiddenOptionId(variableId, optionId)]])
      .filter(([, affectedQuestions]) => affectedQuestions.length > 0);

    return Object.fromEntries(entries);
  }

  private getTakenTokens(excludeIndex?: number): string[] {
    return this.activeSourceGrp$()
      .controls.variables.controls.filter((_, index) => index !== excludeIndex)
      .map((variableCtrl) => variableCtrl.value.variableToken)
      .filter((token) => !!token);
  }

  private buildConfirmDialog(idToRemove: string): MatDialogRef<ConfirmDialogComponent> {
    const affectedStandardQuestions = this.surveyEditorService.surveyEditor$().affectedQuestionsOnConditionById(idToRemove);
    const affectedHiddenQuestion = this.surveyEditorService.surveyEditor$().affectedHiddenQuestionsOnConditionById(idToRemove);
    const affectedQuestions = affectedStandardQuestions.concat(affectedHiddenQuestion);

    let confirmMessage = '';
    if (affectedQuestions.length > 0) {
      confirmMessage = `This variable is part of a condition on <strong>${affectedQuestions.join(', ')}</strong>.</br>`;
    }

    confirmMessage = `${confirmMessage} Are you sure you want to remove this variable?`;

    return this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Confirm', confirmText: confirmMessage, confirmBtn: 'Remove', color: 'warn' }
    });
  }
}
